'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { Trophy, Medal, Filter, Clock as ClockIcon } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface LeaderboardEntry {
  rank: number;
  name: string;
  school: string;
  grade: number | null;
  city: string;
  state: string;
  country: string;
  olympiad: string;
  score: number;
  scoreTotal: number;
  percentile: number | null;
  timeTaken: string;
}

const GRADES = ['All', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

function formatTime(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '—';
  return (seconds / 60).toFixed(2);
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-400 text-white font-bold text-sm"><Trophy size={14} /></span>;
  if (rank === 2) return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-400 text-white font-bold text-sm"><Medal size={14} /></span>;
  if (rank === 3) return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-600 text-white font-bold text-sm"><Medal size={14} /></span>;
  return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground font-bold text-sm">{rank}</span>;
}

export default function LeaderboardPage() {
  const supabase = createClient();

  const [allEntries, setAllEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter options derived from user_profiles
  const [countries, setCountries] = useState<string[]>([]);
  const [allStates, setAllStates] = useState<string[]>([]);
  const [allCities, setAllCities] = useState<string[]>([]);

  // Active filters
  const [filterCountry, setFilterCountry] = useState('All');
  const [filterState, setFilterState] = useState('All');
  const [filterCity, setFilterCity] = useState('All');
  const [filterGrade, setFilterGrade] = useState('All');
  const [filterOlympiad, setFilterOlympiad] = useState('All');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        // Step 1: Fetch completed exam results
        const { data: results, error: resultsError } = await supabase
          .from('exam_results')
          .select('id, user_id, olympiad_name, score_obtained, percentile, global_rank, time_taken_seconds, exam_status, show_name')
          .eq('exam_status', 'completed')
          .order('score_obtained', { ascending: false });

        if (resultsError) throw resultsError;

        if (!results || results.length === 0) {
          setAllEntries([]);
          setLoading(false);
          return;
        }

        // Step 2: Collect unique user IDs
        const userIds = Array.from(new Set(results.map((r: any) => r.user_id).filter(Boolean)));

        // Step 3: Fetch user profiles for those users
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, full_name, grade, city, state_name, country_iso_code, school_id')
          .in('id', userIds);

        if (profilesError) throw profilesError;

        // Step 4: Fetch countries for ISO code → name mapping
        const isoCodesRaw = (profiles || []).map((p: any) => p.country_iso_code).filter(Boolean);
        const isoCodes = Array.from(new Set(isoCodesRaw));
        let countryMap: Record<string, string> = {};
        if (isoCodes.length > 0) {
          const { data: countriesData } = await supabase
            .from('countries')
            .select('iso_code, name')
            .in('iso_code', isoCodes);
          (countriesData || []).forEach((c: any) => {
            countryMap[c.iso_code?.trim()] = c.name;
          });
        }

        // Step 5: Fetch schools
        const schoolIds = Array.from(new Set((profiles || []).map((p: any) => p.school_id).filter(Boolean)));
        let schoolMap: Record<string, string> = {};
        if (schoolIds.length > 0) {
          const { data: schoolsData } = await supabase
            .from('schools')
            .select('id, name')
            .in('id', schoolIds);
          (schoolsData || []).forEach((s: any) => {
            schoolMap[s.id] = s.name;
          });
        }

        // Step 6: Build profile lookup map
        const profileMap: Record<string, any> = {};
        (profiles || []).forEach((p: any) => {
          profileMap[p.id] = p;
        });

        // Step 7: Build leaderboard entries
        let entries: LeaderboardEntry[] = results.map((r: any, idx: number) => {
          const profile = profileMap[r.user_id];
          const isoCode = profile?.country_iso_code?.trim();
          const countryName = isoCode ? (countryMap[isoCode] ?? isoCode) : '—';
          const schoolName = profile?.school_id ? (schoolMap[profile.school_id] ?? '—') : '—';

          let displayName = 'Anonymous';
          if (profile?.full_name) {
            if (r.show_name !== false) {
              displayName = profile.full_name;
            } else {
              const parts = profile.full_name.split(' ');
              const first = parts[0] ?? '';
              const lastInitial = parts[1]?.[0] ? ` ${parts[1][0]}.` : '';
              displayName = first.charAt(0) + '*'.repeat(Math.max(first.length - 1, 2)) + lastInitial;
            }
          }

          return {
            rank: r.global_rank ?? idx + 1,
            name: displayName,
            school: schoolName,
            grade: profile?.grade ? Number(profile.grade) : null,
            city: profile?.city ?? '—',
            state: profile?.state_name ?? '—',
            country: countryName,
            olympiad: r.olympiad_name ?? '—',
            score: r.score_obtained ?? 0,
            scoreTotal: 50,
            percentile: r.percentile ?? null,
            timeTaken: formatTime(r.time_taken_seconds),
          };
        });

        // Sort by rank
        entries.sort((a, b) => a.rank - b.rank);
        setAllEntries(entries);

        // Build filter options from entries
        const uniqueCountries = Array.from(new Set(entries.map((e) => e.country).filter((c) => c !== '—'))).sort();
        const uniqueStates = Array.from(new Set(entries.map((e) => e.state).filter((s) => s !== '—'))).sort();
        const uniqueCities = Array.from(new Set(entries.map((e) => e.city).filter((c) => c !== '—'))).sort();

        setCountries(uniqueCountries);
        setAllStates(uniqueStates);
        setAllCities(uniqueCities);
      } catch (err: any) {
        setError(err?.message ?? 'Failed to load leaderboard data.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Unique olympiad names from fetched data
  const olympiadOptions = useMemo(() => {
    const names = Array.from(new Set(allEntries.map((e) => e.olympiad).filter((o) => o !== '—'))).sort();
    return ['All', ...names];
  }, [allEntries]);

  // Filtered states based on selected country
  const filteredStates = useMemo(() => {
    if (filterCountry === 'All') return allStates;
    return Array.from(new Set(
      allEntries
        .filter((e) => e.country === filterCountry)
        .map((e) => e.state)
        .filter((s) => s !== '—')
    )).sort();
  }, [filterCountry, allEntries, allStates]);

  // Filtered cities based on selected country/state
  const filteredCities = useMemo(() => {
    let entries = allEntries;
    if (filterCountry !== 'All') entries = entries.filter((e) => e.country === filterCountry);
    if (filterState !== 'All') entries = entries.filter((e) => e.state === filterState);
    return Array.from(new Set(entries.map((e) => e.city).filter((c) => c !== '—'))).sort();
  }, [filterCountry, filterState, allEntries]);

  const filtered = useMemo(() => {
    return allEntries.filter((entry) => {
      if (filterCountry !== 'All' && entry.country !== filterCountry) return false;
      if (filterState !== 'All' && entry.state !== filterState) return false;
      if (filterCity !== 'All' && entry.city !== filterCity) return false;
      if (filterGrade !== 'All' && entry.grade !== Number(filterGrade)) return false;
      if (filterOlympiad !== 'All' && entry.olympiad !== filterOlympiad) return false;
      return true;
    });
  }, [allEntries, filterCountry, filterState, filterCity, filterGrade, filterOlympiad]);

  function handleCountryChange(val: string) {
    setFilterCountry(val);
    setFilterState('All');
    setFilterCity('All');
  }

  function handleStateChange(val: string) {
    setFilterState(val);
    setFilterCity('All');
  }

  return (
    <main className="min-h-screen bg-background pt-16">
      {/* Header */}
      <section className="bg-hero-gradient py-14">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-semibold text-white mb-4">
            <Trophy size={12} /> Global Rankings 2026
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-3">Global Leaderboard</h1>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-4">
            See how students rank globally across all olympiads. Rankings update in real-time as results are submitted.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/20 border border-amber-400/40 rounded-xl text-amber-200 text-sm font-semibold">
            🏆 Top 3 winners per country will be awarded a cash prize
          </div>
        </div>
      </section>

      {/* Sticky Filters */}
      <div className="sticky top-16 z-40 bg-card border-b border-border shadow-md">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={14} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Filter Rankings</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Country</label>
              <select
                value={filterCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary"
              >
                <option value="All">All Countries</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">State / Province</label>
              <select
                value={filterState}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary"
              >
                <option value="All">All States</option>
                {filteredStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">City</label>
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary"
              >
                <option value="All">All Cities</option>
                {filteredCities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Grade</label>
              <select
                value={filterGrade}
                onChange={(e) => setFilterGrade(e.target.value)}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary"
              >
                {GRADES.map((g) => <option key={g} value={g}>{g === 'All' ? 'All Grades' : `Grade ${g}`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Olympiad</label>
              <select
                value={filterOlympiad}
                onChange={(e) => setFilterOlympiad(e.target.value)}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary"
              >
                {olympiadOptions.map((o) => <option key={o} value={o}>{o === 'All' ? 'All Olympiads' : o}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <section className="py-10">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading leaderboard…</p>
              ) : error ? (
                <p className="text-sm text-red-500">{error}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-semibold text-foreground">{filtered.length}</span> results
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <ClockIcon size={11} />
                ★ Global leaderboard updates every hour
              </p>
            </div>
            <p className="text-xs text-muted-foreground">* Some names are anonymised for privacy</p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-card border border-border rounded-2xl overflow-hidden shadow-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">School</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Grade</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">City</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">State</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Country</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Olympiad</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time Taken</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="border-b border-border">
                        {Array.from({ length: 10 }).map((_, j) => (
                          <td key={j} className="px-4 py-3.5">
                            <div className="h-4 bg-muted rounded animate-pulse w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-muted-foreground text-sm">
                        No results match your filters. Try adjusting the criteria.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((entry, idx) => (
                      <tr
                        key={`lb-row-${entry.rank}-${idx}`}
                        className={`border-b border-border last:border-0 transition-colors hover:bg-muted/30 ${idx < 3 ? 'bg-amber-50/30' : ''}`}
                      >
                        <td className="px-4 py-3.5">
                          <RankBadge rank={entry.rank} />
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-semibold text-foreground">{entry.name}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-muted-foreground">{entry.school}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-foreground">{entry.grade ? `Grade ${entry.grade}` : '—'}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-muted-foreground">{entry.city}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-muted-foreground">{entry.state}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-muted-foreground">{entry.country}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-secondary/10 text-secondary rounded-md">{entry.olympiad}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-mono text-muted-foreground flex items-center gap-1">
                            <ClockIcon size={12} />{entry.timeTaken}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <span className="text-sm font-bold font-mono text-foreground">{entry.score}</span>
                          <span className="text-xs text-muted-foreground ml-1">/ 50</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-4 shadow-card animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">No results match your filters.</div>
            ) : (
              filtered.map((entry, idx) => (
                <div key={`lb-card-${entry.rank}-${idx}`} className="bg-card border border-border rounded-xl p-4 shadow-card">
                  <div className="flex items-center gap-3 mb-3">
                    <RankBadge rank={entry.rank} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground">{entry.name}</p>
                      <p className="text-xs text-muted-foreground">{entry.school}</p>
                    </div>
                    <span className="text-sm font-bold font-mono text-foreground">
                      {entry.score}<span className="text-xs text-muted-foreground font-normal">/50</span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 text-xs bg-secondary/10 text-secondary rounded-md">{entry.olympiad}</span>
                    {entry.grade && <span className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded-md">Grade {entry.grade}</span>}
                    <span className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded-md">{entry.city}, {entry.country}</span>
                    <span className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded-md flex items-center gap-1"><ClockIcon size={10} />{entry.timeTaken}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
